import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import useScrollAfterReveal from '../../hooks/useScrollAfterReveal';
import { ChevronLeftIcon, ChevronRightIcon, BoltIcon } from '@heroicons/react/20/solid';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  subMonths,
  addMonths,
  format,
  isToday,
  isSameMonth,
  isSameDay,
  isBefore,
  startOfDay,
} from 'date-fns';
import AppointmentTypeSelector from './AppointmentTypeSelector';
import ThemedButton from '../Shared/ThemedButton';
import useTimeSlots from '../../hooks/useTimeSlots';
import useNextAvailableSlot from '../../hooks/useNextAvailableSlot';
import { getTimezoneName } from '../../utility/timezoneUtils';
import { isDarkMode } from '../../utility/themeVars';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function generateCalendarDays(currentDate, selectedDate) {
  const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 }); // Monday
  const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });

  const days = [];
  let day = start;

  while (day <= end) {
    days.push({
      date: day,
      dateString: format(day, 'yyyy-MM-dd'),
      isCurrentMonth: isSameMonth(day, currentDate),
      isToday: isToday(day),
      isSelected: selectedDate ? isSameDay(day, selectedDate) : false,
      isPast: isBefore(day, startOfDay(new Date())),
    });
    day = addDays(day, 1);
  }

  return days;
}

// Removed hardcoded generateTimeSlots function - now using dynamic backend data

// Split slots into period-of-day buckets so the time-picker reads as three
// scannable groups instead of one long grid. Boundaries match the common
// conversational split used by Calendly / Cal.com / Square Appointments.
const SLOT_SECTIONS = [
  { key: 'morning', label: 'Morning' },
  { key: 'afternoon', label: 'Afternoon' },
  { key: 'evening', label: 'Evening' },
];

function bucketSlots(slots) {
  const buckets = { morning: [], afternoon: [], evening: [] };
  for (const slot of slots) {
    const hour = parseInt(slot.time.split(':')[0], 10);
    if (hour < 12) buckets.morning.push(slot);
    else if (hour < 17) buckets.afternoon.push(slot);
    else buckets.evening.push(slot);
  }
  return buckets;
}

const DateTimeSelectionStep = ({
  serviceLocationTypes,
  selectedAppointmentType,
  onAppointmentTypeChange,
  selectedDate,
  onDateChange,
  selectedTime,
  onTimeChange,
  primaryColor = 'blue-600',
  themeMode = 'light',
  businessName,
  hideAppointmentTypeSelector = false,
  slug,
  availabilityIds,
  timeZoneId,
  bookingPage,
  onRequestCustomTime,
  selectedServices,
  totalServiceDuration,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const timeSlotsRef = useRef(null);
  const scrollAfterReveal = useScrollAfterReveal();
  // Tracks which date we last re-aligned to, so the re-align only fires once
  // per date selection rather than on every slots render.
  const lastAlignedDateRef = useRef(null);

  // Use dynamic time slots from backend
  const { timeSlots, isLoading: isLoadingSlots, error: slotsError, retryFetch } = useTimeSlots(
    slug,
    selectedDate,
    availabilityIds,
    totalServiceDuration
  );

  // Group slots into Morning / Afternoon / Evening. Count badges show only
  // available slots so they reflect what the user can actually book.
  const slotBuckets = useMemo(() => bucketSlots(timeSlots), [timeSlots]);

  // A fully-booked day still returns a full slots array (every entry flagged
  // available:false), so `timeSlots.length === 0` does NOT catch it — that only
  // means the provider has no availability window that day. Without this, a
  // fully-booked day renders a grid of dead buttons and no explanation.
  const hasAnyAvailable = timeSlots.some((s) => s.available);
  const isFullyBooked = timeSlots.length > 0 && !hasAnyAvailable;

  // Use next available slot functionality
  const { 
    findNextAvailable, 
    isLoading: isLoadingNextAvailable, 
    error: nextAvailableError,
    result: nextAvailableResult 
  } = useNextAvailableSlot(slug);

  const days = useMemo(() => generateCalendarDays(currentMonth, selectedDate), [currentMonth, selectedDate]);
  const isDark = isDarkMode(themeMode);

  // Auto-scroll to time slots when they become visible, waiting for the
  // reveal animation to finish so the slot section has settled into position.
  useEffect(() => {
    if (showTimeSlots) {
      scrollAfterReveal(timeSlotsRef);
    }
  }, [showTimeSlots, scrollAfterReveal]);

  const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  // Reset retry count when date changes
  useEffect(() => {
    setRetryCount(0);
  }, [selectedDate]);

  // Re-align the slots section once after slots finish loading for a given date
  // so the top of the slot grid is visible without manual scroll. Skipped when
  // there are no slots or an error (nothing useful to align to).
  // Date objects are compared by their yyyy-MM-dd string key to prevent
  // spurious re-scrolls when two clicks on the same cell produce non-identical
  // Date instances.
  useEffect(() => {
    const dateKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
    if (
      !isLoadingSlots &&
      !slotsError &&
      timeSlots.length > 0 &&
      showTimeSlots &&
      dateKey &&
      lastAlignedDateRef.current !== dateKey
    ) {
      lastAlignedDateRef.current = dateKey;
      scrollAfterReveal(timeSlotsRef, { block: 'nearest' });
    }
  }, [isLoadingSlots, slotsError, timeSlots.length, showTimeSlots, selectedDate, scrollAfterReveal]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    retryFetch();
  };

  const handleDateSelect = useCallback((day) => {
    if (day.isPast) return;

    // Clear selected time when date changes to prevent stale selections
    if (selectedTime) {
      onTimeChange(null);
    }

    onDateChange(day.date);
    setShowTimeSlots(true);
    // Scroll is handled by the useEffect that watches showTimeSlots, after the
    // time-slots section has mounted (timeSlotsRef.current is still null here).
  }, [onDateChange, onTimeChange, selectedTime]);

  const handleTimeSelect = useCallback((slot) => {
    if (!slot.available) return;
    onTimeChange(slot.time, true);
  }, [onTimeChange]);

  const handleFindNextAvailable = useCallback(async () => {
    if (!availabilityIds || availabilityIds.length === 0) {
      return;
    }

    try {
      const result = await findNextAvailable(availabilityIds, null, totalServiceDuration);

      if (result && result.available) {
        const dateOnly = new Date(`${result.date}T00:00:00`);
        onDateChange(dateOnly);
        onTimeChange(result.time, true);
        setShowTimeSlots(true);
        setCurrentMonth(dateOnly);
      }
    } catch (error) {
      console.error('Error finding next available slot:', error);
    }
  }, [availabilityIds, totalServiceDuration, findNextAvailable, onDateChange, onTimeChange]);

  const getDateButtonStyles = useCallback((day) => {
    if (day.isPast) return 'border-transparent text-themed-secondary cursor-not-allowed';
    if (day.isSelected) return 'selected-themed';
    if (!day.isCurrentMonth) return 'border-transparent text-themed-secondary cursor-pointer';
    if (day.isToday) return 'border-transparent today-themed text-themed-primary';
    return 'border-transparent text-themed-primary hover-themed-tint';
  }, []);

  // Three states, deliberately ordered by visual weight so they can't be
  // confused: selected (filled) > available (card + border + hover) >
  // unavailable (sunken, struck through, no hover).
  const getTimeSlotStyles = useCallback((slot) => {
    if (!slot.available) return 'slot-themed-disabled';
    if (selectedTime === slot.time) return 'selected-themed';
    return 'bg-themed-card text-themed-primary border-themed hover-themed-tint';
  }, [selectedTime]);


  return (
    <div className="space-y-8">
      {/* Step 1: Appointment Type Selection - Only show if not hidden */}
      {!hideAppointmentTypeSelector && (
        <AppointmentTypeSelector
          serviceLocationTypes={serviceLocationTypes}
          selectedType={selectedAppointmentType}
          onTypeChange={onAppointmentTypeChange}
          primaryColor={primaryColor}
          themeMode={themeMode}
          businessName={businessName}
        />
      )}

      {/* Step 2: Date Selection */}
      {(selectedAppointmentType || hideAppointmentTypeSelector) && (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-themed-primary mb-2">
              Select a date
            </h3>
            <p className="text-sm text-themed-secondary">
              Choose your preferred appointment date
            </p>
          </div>


          {/* Next Available Error Message */}
          {nextAvailableError && (
            <div className={`p-3 ${isDark ? 'bg-red-900/20' : 'bg-signal-red-50'} rounded-lg border ${isDark ? 'border-red-800' : 'border-signal-red-100'}`}>
              <p className={`text-sm ${isDark ? 'text-red-300' : 'text-signal-red-700'}`}>{nextAvailableError}</p>
            </div>
          )}

          {/* Next Available Result Message */}
          {nextAvailableResult && !nextAvailableResult.available && (
            <div className={`p-3 ${isDark ? 'bg-signal-amber-500/20' : 'bg-signal-amber-50'} rounded-lg border ${isDark ? 'border-signal-amber-500' : 'border-signal-amber-100'}`}>
              <p className={`text-sm ${isDark ? 'text-signal-amber-100' : 'text-signal-amber-600'}`}>
                No available appointments found in the next {nextAvailableResult.searchedDays || 60} days. Please contact us directly or try selecting a specific date.
              </p>
            </div>
          )}

          {/* Calendar */}
          <div className="bg-themed-card rounded-lg border border-themed p-4">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                aria-label="Previous month"
                onClick={goToPreviousMonth}
                className="p-2 text-themed-secondary hover:text-themed-primary transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5" aria-hidden="true" />
              </button>
              <h2 className="text-lg font-semibold text-themed-primary">
                {format(currentMonth, 'MMMM yyyy')}
              </h2>
              <div className="flex items-center space-x-2">
                {/* Quick Find Button - Always visible, disabled until availability data loads */}
                <button
                  type="button"
                  onClick={handleFindNextAvailable}
                  disabled={isLoadingNextAvailable || !availabilityIds || availabilityIds.length === 0}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border btn-themed-secondary disabled:opacity-50 transition-colors duration-200"
                >
                  <BoltIcon className="w-3.5 h-3.5" />
                  {isLoadingNextAvailable ? 'Finding...' : 'Find next available'}
                </button>
                <button
                  type="button"
                  aria-label="Next month"
                  onClick={goToNextMonth}
                  className="p-2 text-themed-secondary hover:text-themed-primary transition-colors"
                >
                  <ChevronRightIcon className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 text-center text-sm">
              {/* Day Headers */}
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <div key={day} className="py-2 text-themed-secondary font-medium">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {days.map((day, dayIdx) => (
                <button
                  key={day.dateString}
                  type="button"
                  onClick={() => handleDateSelect(day)}
                  disabled={day.isPast}
                  className={classNames(
                    'py-2 px-1 text-sm rounded-lg border transition-colors duration-200',
                    getDateButtonStyles(day)
                  )}
                >
                  <time dateTime={day.dateString}>
                    {format(day.date, 'd')}
                  </time>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Time Selection */}
      {selectedDate && showTimeSlots && (
        <div ref={timeSlotsRef} className="space-y-4 scroll-mt-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <div>
            <h3 className="text-lg font-semibold text-themed-primary mb-2">
              Select a time on {format(selectedDate, 'EEEE, MMMM d')}
            </h3>
            {timeZoneId && (
              <p className="text-sm font-medium text-themed-secondary mb-1">
                Times shown in {getTimezoneName(timeZoneId, selectedDate) || timeZoneId.replace('_', ' ')}
              </p>
            )}
            <p className="text-sm text-themed-secondary">
              Choose your preferred appointment time
            </p>
          </div>

          {/* Loading State */}
          {isLoadingSlots && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 spinner-themed"></div>
              <span className="ml-3 text-sm text-themed-secondary">Loading available times...</span>
            </div>
          )}

          {/* Error State */}
          {slotsError && (
            <div className={`p-4 ${isDark ? 'bg-signal-red-500/20' : 'bg-signal-red-50'} rounded-lg border ${isDark ? 'border-signal-red-500' : 'border-signal-red-100'}`}>
              <div className="flex items-start">
                <svg className="w-5 h-5 text-signal-red-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-signal-red-100' : 'text-signal-red-600'}`}>Unable to load available times</p>
                  <p className={`text-sm ${isDark ? 'text-signal-red-500' : 'text-signal-red-700'} mt-1`}>{slotsError}</p>
                  {retryCount < 2 ? (
                    <button
                      onClick={handleRetry}
                      className={`text-xs ${isDark ? 'text-signal-red-500 hover:text-signal-red-100' : 'text-signal-red-700 hover:text-signal-red-700'} underline mt-1`}
                    >
                      Try again
                    </button>
                  ) : (
                    <div className="mt-2 space-y-1">
                      <p className={`text-xs ${isDark ? 'text-signal-red-500' : 'text-signal-red-700'}`}>
                        Having trouble? Try a different date{bookingPage?.allowOutsideAvailability && onRequestCustomTime ? ', or request a custom time.' : '.'}
                      </p>
                      {bookingPage?.allowOutsideAvailability && onRequestCustomTime && (
                        <button
                          onClick={onRequestCustomTime}
                          className={`text-xs ${isDark ? 'text-signal-red-500 hover:text-red-300' : 'text-signal-red-700 hover:text-signal-red-700'} underline block`}
                        >
                          Request a custom time instead
                        </button>
                      )}
                      <button
                        onClick={handleRetry}
                        className={`text-xs ${isDark ? 'text-signal-red-500 hover:text-red-300' : 'text-signal-red-700 hover:text-signal-red-700'} underline block`}
                      >
                        Try once more
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* No Slots Available — covers both "provider has no availability window
              this day" (timeSlots empty) and "every slot is booked" (isFullyBooked).
              The latter used to fall through to a bare grid of dead buttons. */}
          {!isLoadingSlots && !slotsError && (timeSlots.length === 0 || isFullyBooked) && (
            <div className="p-4 bg-themed-card rounded-lg border border-themed">
              <div className="text-center">
                <div className="flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-themed-secondary mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium text-themed-primary">
                    {isFullyBooked
                      ? 'Fully booked on this date'
                      : 'No available times for this date'}
                  </p>
                </div>

                {/* CTA: no slots available */}
                {bookingPage?.allowOutsideAvailability && onRequestCustomTime ? (
                  <div className="space-y-3">
                    <p className="text-sm text-themed-secondary">
                      {isFullyBooked
                        ? 'Every time on this date is taken — request a custom time instead.'
                        : 'No openings today — request a custom time instead.'}
                    </p>
                    <p className="text-xs text-themed-secondary">
                      This booking will require approval.
                    </p>
                    <ThemedButton
                      onClick={onRequestCustomTime}
                      primaryColor={primaryColor}
                      themeMode={themeMode}
                      variant="secondary"
                      size="medium"
                      className="inline-flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Request Custom Time
                    </ThemedButton>
                  </div>
                ) : (
                  <p className="text-sm text-themed-secondary mt-1">
                    Try a different date, or use "Find next available" above to jump to the first opening.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Time Slots — grouped by period of day. Gated on hasAnyAvailable, not
              timeSlots.length: a fully-booked day returns a full slots array, and
              rendering it here would stack a grid of dead buttons (plus a second,
              contradictory "Request Custom Time" CTA) under the fully-booked panel. */}
          {!isLoadingSlots && !slotsError && hasAnyAvailable && (
            <div className="space-y-6">
              {SLOT_SECTIONS.map(({ key, label }) => {
                const bucket = slotBuckets[key];
                if (bucket.length === 0) return null;
                const availableCount = bucket.filter((s) => s.available).length;
                return (
                  <div key={key} className="space-y-3">
                    <div className="flex items-baseline justify-between border-b border-themed pb-2">
                      <h4 className="text-sm font-semibold text-themed-primary">{label}</h4>
                      <span className="text-xs text-themed-secondary">
                        {availableCount === 0 ? 'Fully booked' : `${availableCount} available`}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {bucket.map((slot) => (
                        <button
                          key={slot.time}
                          type="button"
                          onClick={() => handleTimeSelect(slot)}
                          disabled={!slot.available}
                          // The strikethrough conveys "unavailable" visually; a
                          // disabled button announces no state, so name it here too.
                          // Comma, not an em-dash: screen readers announce U+2014
                          // inconsistently (some read it aloud, some skip it).
                          aria-label={
                            slot.available
                              ? slot.display
                              : `${slot.display}, unavailable`
                          }
                          className={classNames(
                            'py-3 px-3 text-sm font-medium rounded-lg border transition-colors duration-200',
                            getTimeSlotStyles(slot)
                          )}
                        >
                          {slot.display}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* CTA: slots exist but user wants different time */}
              {bookingPage?.allowOutsideAvailability && onRequestCustomTime && (
                <div className="pt-4 border-t border-themed">
                  <div className="text-center">
                    <p className="text-sm text-themed-secondary mb-3">
                      Don't see a time that works?
                    </p>
                    <ThemedButton
                      onClick={onRequestCustomTime}
                      primaryColor={primaryColor}
                      themeMode={themeMode}
                      variant="secondary"
                      size="medium"
                      className="inline-flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Request Custom Time
                    </ThemedButton>
                    <p className="text-xs text-themed-secondary mt-2">
                      Subject to approval
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default DateTimeSelectionStep;